import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Slider,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField
} from '@mui/material';
import { Refresh, Add, CheckCircle, Edit, Archive, Delete, Block, Visibility, ContentCopy, Unarchive } from '@mui/icons-material';
import { activateVersion, updateRollout, archiveVersion, unarchiveVersion, deactivateVersion, deleteVersion } from '../../services/deviceAppUpdateService';

export default function VersionsTable({ updates, loading, onRefresh, onAddVersion, onEditVersion }) {
  const [editingRollout, setEditingRollout] = useState(null);
  const [rolloutValue, setRolloutValue] = useState(0);
  const [activating, setActivating] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [unarchiving, setUnarchiving] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyDialogText, setCopyDialogText] = useState('');

  const handleActivate = async (versionCode) => {
    const version = updates.find(u => u.versionCode === versionCode);
    const activeVersion = updates.find(u => u.status === 'ACTIVE');
    const isDowngrade = activeVersion && version.versionCode < activeVersion.versionCode;

    // Warning specifico per downgrade (NON funzionerà su Android)
    if (isDowngrade) {
      const message = `⚠️ ATTENZIONE: DOWNGRADE RILEVATO\n\n` +
        `Stai tentando di attivare versionCode ${version.versionCode} mentre ${activeVersion.versionCode} è in uso.\n\n` +
        `🚫 PROBLEMA: Android BLOCCA il downgrade per policy di sicurezza.\n` +
        `I dispositivi riceveranno errore: INSTALL_FAILED_VERSION_DOWNGRADE\n\n` +
        `✅ SOLUZIONE RACCOMANDATA (Rollback Funzionale):\n` +
        `1. Recupera il CODICE di v${version.versionName}\n` +
        `2. Crea nuova versione v${version.versionName.split('.').slice(0,2).join('.')}.${parseInt(version.versionName.split('.')[2]) + 2} (versionCode ${activeVersion.versionCode + 1})\n` +
        `3. Pubblica come rollback funzionale (versionCode crescente)\n\n` +
        `Consulta la sezione "📖 Guida → Rollback Funzionale" per dettagli.\n\n` +
        `Vuoi procedere comunque? (NON funzionerà)`;

      const confirm = window.confirm(message);
      if (!confirm) return;
    } else {
      const confirm = window.confirm(
        `Attivare versione ${version.versionName}?\n\n` +
        `Questo imposterà ${version.versionName} come versione attiva.`
      );
      if (!confirm) return;
    }

    try {
      setActivating(versionCode);
      await activateVersion(versionCode);
      onRefresh();
    } catch (error) {
      alert(`Errore attivazione versione: ${error.message}`);
    } finally {
      setActivating(null);
    }
  };

  const handleDeactivate = async (versionCode) => {
    const version = updates.find(u => u.versionCode === versionCode);
    const confirm = window.confirm(
      `Disattivare versione ${version.versionName}?\n\n` +
      `La versione tornerà a stato INACTIVE e potrai modificarla o eliminarla.`
    );

    if (!confirm) return;

    try {
      setDeactivating(versionCode);
      await deactivateVersion(versionCode);
      onRefresh();
    } catch (error) {
      alert(`Errore disattivazione versione: ${error.message}`);
    } finally {
      setDeactivating(null);
    }
  };

  const handleArchive = async (versionCode) => {
    const version = updates.find(u => u.versionCode === versionCode);
    const confirm = window.confirm(
      `Archiviare versione ${version.versionName}?\n\n` +
      `La versione non sarà più distribuita ma rimarrà nello storico.`
    );

    if (!confirm) return;

    try {
      setArchiving(versionCode);
      await archiveVersion(versionCode);
      onRefresh();
    } catch (error) {
      alert(`Errore archiviazione versione: ${error.message}`);
    } finally {
      setArchiving(null);
    }
  };

  const handleUnarchive = async (versionCode) => {
    const version = updates.find(u => u.versionCode === versionCode);
    const confirm = window.confirm(
      `Ripristinare versione ${version.versionName}?\n\n` +
      `La versione tornerà nello stato INACTIVE e sarà nuovamente visibile.`
    );

    if (!confirm) return;

    try {
      setUnarchiving(versionCode);
      await unarchiveVersion(versionCode);
      onRefresh();
    } catch (error) {
      alert(`Errore ripristino versione: ${error.message}`);
    } finally {
      setUnarchiving(null);
    }
  };

  const handleDelete = async (versionCode) => {
    const version = updates.find(u => u.versionCode === versionCode);
    const confirm = window.confirm(
      `⚠️ ELIMINARE definitivamente versione ${version.versionName} (${versionCode})?\n\n` +
      `Questa azione è IRREVERSIBILE.`
    );

    if (!confirm) return;

    try {
      setDeleting(versionCode);
      await deleteVersion(versionCode);
      onRefresh();
    } catch (error) {
      alert(`Errore eliminazione versione: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleViewDetails = (update) => {
    console.log('Opening details for version:', update);
    setSelectedVersion(update);
    setViewDetailsOpen(true);
  };

  const handleCopyField = async (fieldName, value) => {
    console.log('handleCopyField called:', fieldName, value);
    
    // Controlla se il valore esiste
    if (!value || value === 'undefined' || value === 'null') {
      console.warn('Valore non disponibile:', fieldName, value);
      alert(`Impossibile copiare ${fieldName}: valore non disponibile`);
      return;
    }

    const textToCopy = String(value);
    
    // Prova prima con Clipboard API moderna
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        alert(`${fieldName} copiato negli appunti!`);
        return;
      }
    } catch (err) {
      console.warn('Clipboard API fallita, uso dialog manuale:', err);
    }
    
    // Se Clipboard API non disponibile o fallisce, mostra dialog con testo selezionabile
    setCopyDialogText(textToCopy);
    setCopyDialogOpen(true);
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <Chip label="ATTIVA" color="success" size="medium" icon={<CheckCircle />} sx={{ fontWeight: 'bold' }} />;
      case 'ARCHIVED':
        return <Chip label="ARCHIVIATA" color="info" size="small" sx={{ ml: 1 }} />;
      case 'INACTIVE':
        return <Chip label="INATTIVA" color="default" size="small" sx={{ ml: 1 }} />;
      default:
        return null;
    }
  };

  const handleRolloutUpdate = async () => {
    try {
      await updateRollout(editingRollout.versionCode, {
        rolloutPercent: rolloutValue
      });
      setEditingRollout(null);
      onRefresh();
    } catch (error) {
      alert(`Errore aggiornamento rollout: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Versioni App</Typography>
        <Box>
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={onAddVersion}
            sx={{ mr: 1 }}
          >
            Aggiungi Versione
          </Button>
          <IconButton onClick={onRefresh}>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Versione</TableCell>
              <TableCell>Codice</TableCell>
              <TableCell align="center">Attiva</TableCell>
              <TableCell>Distribuzione %</TableCell>
              <TableCell align="center">Obbligatorio</TableCell>
              <TableCell>Dispositivi Aggiornati</TableCell>
              <TableCell>Creata</TableCell>
              <TableCell>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {updates.map((update) => (
              <TableRow 
                key={update.versionCode}
                sx={update.status === 'ACTIVE' ? {
                  backgroundColor: 'rgba(46, 125, 50, 0.08)',
                  borderLeft: '4px solid #2e7d32',
                  '&:hover': {
                    backgroundColor: 'rgba(46, 125, 50, 0.12)'
                  }
                } : {}}
              >
                <TableCell>
                  <strong>{update.versionName}</strong>
                </TableCell>
                <TableCell>{update.versionCode}</TableCell>
                <TableCell align="center">
                  {getStatusChip(update.status)}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Slider
                      value={update.rolloutPercent}
                      disabled
                      sx={{ width: 100 }}
                    />
                    <Typography variant="body2">{update.rolloutPercent}%</Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingRollout(update);
                        setRolloutValue(update.rolloutPercent);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {update.mandatory ? (
                    <Chip label="SÌ" color="error" size="small" />
                  ) : (
                    <Chip label="NO" variant="outlined" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {update.devicesOnVersion} / {update.totalActiveDevices}
                  <Typography variant="caption" display="block" color="text.secondary">
                    ({update.adoptionPercent}%)
                  </Typography>
                </TableCell>
                <TableCell>
                  {new Date(update.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Tooltip title="Visualizza Dettagli">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleViewDetails(update)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Attiva Versione">
                      <span>
                        <Button
                          size="small"
                          variant={update.status === 'ACTIVE' ? "outlined" : "contained"}
                          disabled={update.status === 'ACTIVE' || update.status === 'ARCHIVED' || activating === update.versionCode}
                          onClick={() => handleActivate(update.versionCode)}
                        >
                          {activating === update.versionCode ? <CircularProgress size={16} /> : 'Attiva'}
                        </Button>
                      </span>
                    </Tooltip>

                    <Tooltip title="Disattiva Versione">
                      <span>
                        <IconButton
                          size="small"
                          color="warning"
                          disabled={update.status !== 'ACTIVE' || deactivating === update.versionCode}
                          onClick={() => handleDeactivate(update.versionCode)}
                        >
                          {deactivating === update.versionCode ? <CircularProgress size={20} /> : <Block />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Modifica Versione">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={update.status === 'ACTIVE'}
                          onClick={() => onEditVersion(update)}
                        >
                          <Edit />
                        </IconButton>
                      </span>
                    </Tooltip>
                    
                    <Tooltip title={update.status === 'ARCHIVED' ? "Ripristina Versione (torna INACTIVE)" : "Archivia Versione"}>
                      <span>
                        <IconButton
                          size="small"
                          color={update.status === 'ARCHIVED' ? 'success' : 'info'}
                          disabled={
                            (update.status === 'ARCHIVED' && unarchiving === update.versionCode) ||
                            (update.status !== 'ARCHIVED' && (update.status === 'ACTIVE' || archiving === update.versionCode))
                          }
                          onClick={() => update.status === 'ARCHIVED' ? handleUnarchive(update.versionCode) : handleArchive(update.versionCode)}
                        >
                          {(archiving === update.versionCode || unarchiving === update.versionCode) ? (
                            <CircularProgress size={20} />
                          ) : (
                            update.status === 'ARCHIVED' ? <Unarchive /> : <Archive />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Elimina Versione">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={update.status === 'ACTIVE' || deleting === update.versionCode}
                          onClick={() => handleDelete(update.versionCode)}
                        >
                          {deleting === update.versionCode ? <CircularProgress size={20} /> : <Delete />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Rollout Dialog */}
      <Dialog open={!!editingRollout} onClose={() => setEditingRollout(null)}>
        <DialogTitle>Modifica Distribuzione - {editingRollout?.versionName}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, width: 400 }}>
            <Typography gutterBottom>Percentuale Distribuzione: {rolloutValue}%</Typography>
            <Slider
              value={rolloutValue}
              onChange={(e, val) => setRolloutValue(val)}
              marks={[
                { value: 0, label: '0%' },
                { value: 25, label: '25%' },
                { value: 50, label: '50%' },
                { value: 75, label: '75%' },
                { value: 100, label: '100%' }
              ]}
              step={5}
              min={0}
              max={100}
            />
            <Typography variant="caption" color="text.secondary">
              Distribuzione deterministica: i dispositivi sono assegnati a bucket 0-99 basati sull'hash del loro ID.
              Solo i dispositivi nei bucket 0-{rolloutValue-1} riceveranno questo aggiornamento.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingRollout(null)}>Annulla</Button>
          <Button variant="contained" onClick={handleRolloutUpdate}>
            Aggiorna Distribuzione
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog 
        open={viewDetailsOpen} 
        onClose={() => setViewDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Dettagli Versione {selectedVersion?.versionName}
          <Chip 
            label={selectedVersion?.status} 
            color={selectedVersion?.status === 'ACTIVE' ? 'success' : 'default'}
            size="small"
            sx={{ ml: 2 }}
          />
        </DialogTitle>
        <DialogContent dividers>
          {selectedVersion && (
            <Box sx={{ '& > *': { mb: 2 } }}>
              {/* Version Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Version Name</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">{selectedVersion.versionName}</Typography>
                  <IconButton size="small" onClick={() => handleCopyField('Version Name', selectedVersion.versionName)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Version Code</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">{selectedVersion.versionCode}</Typography>
                  <IconButton size="small" onClick={() => handleCopyField('Version Code', selectedVersion.versionCode)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* APK URL */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">APK URL</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {selectedVersion.apkUrl}
                  </Typography>
                  <IconButton size="small" onClick={() => handleCopyField('APK URL', selectedVersion.apkUrl)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* SHA-256 */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">SHA-256 Hash</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {selectedVersion.sha256}
                  </Typography>
                  <IconButton size="small" onClick={() => handleCopyField('SHA-256', selectedVersion.sha256)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* APK Size */}
              {selectedVersion.apkSizeBytes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Dimensione APK</Typography>
                  <Typography variant="body1">
                    {(selectedVersion.apkSizeBytes / 1024 / 1024).toFixed(2)} MB
                    ({selectedVersion.apkSizeBytes.toLocaleString()} bytes)
                  </Typography>
                </Box>
              )}

              {/* Rollout Config */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Distribuzione</Typography>
                <Typography variant="body1">
                  {selectedVersion.rolloutPercent}% 
                  {selectedVersion.mandatory && <Chip label="OBBLIGATORIO" color="error" size="small" sx={{ ml: 1 }} />}
                </Typography>
              </Box>

              {/* Adoption Stats */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Dispositivi Aggiornati</Typography>
                <Typography variant="body1">
                  {selectedVersion.devicesOnVersion} / {selectedVersion.totalActiveDevices} ({selectedVersion.adoptionPercent}%)
                </Typography>
              </Box>

              {/* Release Notes */}
              {selectedVersion.releaseNotes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Release Notes</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedVersion.releaseNotes}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Timestamps */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Creata il</Typography>
                <Typography variant="body2">
                  {new Date(selectedVersion.createdAt).toLocaleString('it-IT')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Ultima modifica</Typography>
                <Typography variant="body2">
                  {new Date(selectedVersion.updatedAt).toLocaleString('it-IT')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDetailsOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Copy Dialog - Fallback for manual copy */}
      <Dialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Copia Testo</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Seleziona il testo qui sotto e premi Ctrl+C (o Cmd+C su Mac) per copiarlo negli appunti.
          </Alert>
          <TextField
            fullWidth
            multiline
            maxRows={6}
            value={copyDialogText}
            variant="outlined"
            inputProps={{
              readOnly: true,
              style: { fontFamily: 'monospace', fontSize: '0.9rem' }
            }}
            onFocus={(e) => e.target.select()}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
